import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

function Dialog({
    containerOverflow, children, model, footer, fullSize, defaultOverSized, largeSizeModal, mediumSizeModal,
})
{
    const dialogRef = useRef(null);
    const [overSized, setOverSized] = useState(defaultOverSized);
    const prevState = useRef(false);

    let modalSize = fullSize ? ' tw-w-full max-sm:tw-max-w-3xl tw-max-w-[80%]' : 'sm:tw-max-w-lg tw-w-full';
    modalSize = largeSizeModal ? ' tw-w-[1200px] tw-max-w-[1200px] ' : modalSize;
    modalSize = mediumSizeModal && !largeSizeModal ? ' tw-w-[730px] tw-max-w-[730px] ' : modalSize;

    const clickWrapped = (e) =>
    {
        if (e.target.id === 'buttonWrapped')
        {
            closeModal();
        }
    };

    const closeModal = () =>
    {
        model.setter(false);
    };

    const handleDialogResize = () =>
    {
        if (dialogRef.current)
        {
            if (dialogRef.current.offsetHeight + 50 > window.innerHeight)
            {
                setOverSized(true);
            }
        }
    };

    useEffect(() =>
    {
        setImmediate(() =>
        {
            document.body.style.overflow = model.value ? 'hidden' : '';
        });

        const resizeObserver = new ResizeObserver(handleDialogResize);
        if (dialogRef.current)
        {
            resizeObserver.observe(dialogRef.current);
        }

        handleDialogResize();

        if (model.value !== prevState.current)
        {
            setImmediate(() =>
            {
                document.body.style.overflow = model.value ? 'hidden' : '';
            });
        }

        prevState.current = model.value;
        return () =>
        {
            if (dialogRef.current)
            {
                resizeObserver.unobserve(dialogRef.current);
            }
        };
    }, [model]);

    return model.value ? (
        <div className="tw-relative tw-z-[899]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="tw-fixed tw-inset-0 tw-bg-gray-500 tw-bg-opacity-75 tw-transition-opacity"></div>
            <div className={`tw-fixed tw-inset-0 tw-z-[899] tw-overflow-y-auto tw-flex tw-min-h-full tw-justify-center tw-p-4 tw-text-center ${overSized ? 'tw-items-start' : 'tw-items-center'}`} id="DialogMainContainer">
                <button
                    type="button"
                    className="tw-z-[900] tw-cursor-default tw-absolute tw-left-0 tw-top-0 tw-w-full tw-h-full"
                    id="buttonWrapped"
                    onClick={(e) =>
                    {
                        clickWrapped(e);
                    }}
                > </button>
                <div className={`tw-z-[901] tw-overflow-y-visible tw-transform tw-rounded-lg dark:tw-bg-neutral-700 tw-bg-white tw-text-gray-900 tw-text-left tw-shadow-xl tw-transition-all sm:tw-my-8 sm:tw-w-full ${modalSize}`} ref={dialogRef}>
                    <button data-display-label="Close Dialog" type="button" onClick={closeModal} className="dark:tw-text-white tw-absolute tw-top-0 tw-right-0 tw-mx-auto tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-items-center tw-justify-center tw-cursor-pointer sm:tw-mx-0 sm:tw-h-10 sm:tw-w-10">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className={`tw-px-4 tw-pt-5 tw-pb-4 sm:tw-p-6 sm:tw-pb-4 ${containerOverflow}`}>
                        <div className="sm:tw-flex sm:tw-items-start">
                            <div className="tw-flex-grow tw-mt-3 tw-text-center sm:tw-mt-0 sm:tw-text-left dark:tw-text-white tw-max-w-full">
                                { children }
                            </div>
                        </div>
                    </div>
                    {footer && (
                        <div className="tw-bg-gray-50 tw-px-4 tw-py-3 sm:tw-flex sm:tw-flex-row-reverse sm:tw-px-6 dark:tw-bg-neutral-600 tw-rounded-b-lg">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    ) : null;
}

Dialog.defaultProps = {
    containerOverflow: 'tw-overflow-hidden',
    fullSize: false,
    defaultOverSized: false,
    mediumSizeModal: false,
};

Dialog.propTypes = {
    children: PropTypes.node,
    model: PropTypes.shape({ value: PropTypes.bool.isRequired, setter: PropTypes.func }),
    footer: PropTypes.node,
    containerOverflow: PropTypes.string,
    fullSize: PropTypes.bool,
    defaultOverSized: PropTypes.bool,
    largeSizeModal: PropTypes.bool,
    mediumSizeModal: PropTypes.bool,
};

export default Dialog;
